/**
 * #4453 — quem pode ver/abrir atendimento de GRUPO.
 *
 * Grupo só existe de verdade quando a CONEXÃO aceita grupos (`allowGroup`).
 * Se nenhuma conexão ao alcance do usuário aceita, não há o que listar e não
 * há para onde enviar: a aba "Grupos" e a escolha de um contato de grupo no
 * "Novo Ticket" só confundem — o operador manda a mensagem, ela não chega, e
 * isso volta como "não recebi".
 *
 * A regra é um OR entre as conexões que o usuário alcança (decisão do Clovis,
 * 08-10-2026): basta UMA aceitar para o grupo continuar disponível.
 */

/**
 * Conexões que este usuário alcança.
 *
 * - admin / allUserChat=enabled: todas as da empresa.
 * - usuário comum: as vinculadas às filas dele **mais** as que não têm fila
 *   nenhuma — conexão sem fila não é de ninguém em particular, então vale para
 *   todos (sem isso, empresa que não usa fila por conexão perdia a aba inteira).
 */
export function whatsAppsReachableByUser(whatsApps, user) {
  if (!Array.isArray(whatsApps)) return [];

  // Comparação sem diferenciar maiúsculas, como o `canShowAllTickets` do
  // TicketsManagerTabs: o perfil vem do banco e já apareceu como "Admin".
  const canSeeAll =
    user?.profile?.toUpperCase() === "ADMIN" ||
    user?.allUserChat?.toUpperCase() === "ENABLED";
  if (canSeeAll) return whatsApps;

  const userQueueIds = Array.isArray(user?.queues)
    ? user.queues.map((q) => q?.id)
    : [];

  return whatsApps.filter((w) => {
    const queues = Array.isArray(w?.queues) ? w.queues : [];
    if (queues.length === 0) return true;
    return queues.some((q) => userQueueIds.includes(q?.id));
  });
}

/**
 * Existe alguma conexão ao alcance do usuário que aceita grupos?
 *
 * Enquanto a lista ainda não chegou (array vazio no primeiro render) devolve
 * `true` para a aba não sumir e voltar durante o carregamento.
 */
export function hasWhatsAppAllowingGroup(whatsApps, user) {
  if (!Array.isArray(whatsApps) || whatsApps.length === 0) return true;
  return whatsAppsReachableByUser(whatsApps, user).some(
    (w) => w?.allowGroup === true
  );
}

/** A aba "Grupos" exige a permissão do perfil E uma conexão que aceite grupos. */
export function canUseGroups(whatsApps, user) {
  return Boolean(user?.allowGroup) && hasWhatsAppAllowingGroup(whatsApps, user);
}

/**
 * A aba "Grupos" pode sumir com ela ABERTA — o admin desliga "permitir grupos"
 * na conexão enquanto o atendente está nela. O `<Tabs>` do MUI fica então com
 * um `value` sem `<Tab>` correspondente: nenhuma aba marcada e nenhuma lista
 * renderizada, ou seja, tela em branco (nota #8895 da #4453).
 *
 * Devolve a aba para onde ir, ou `null` quando não há nada a fazer — assim o
 * efeito que chama isto não dispara `setState` à toa.
 */
export function tabAfterGroupAccessLost(tabOpen, showGroupTab) {
  if (showGroupTab) return null;
  return tabOpen === "group" ? "open" : null;
}

/**
 * Contatos que podem aparecer no "Novo Ticket". Sem conexão que aceite grupos,
 * o contato de grupo não pode nem ser oferecido: o atendente escolhia, o
 * ticket nascia e a mensagem não ia a lugar nenhum (nota #8896 da #4453).
 */
export function filterContactsByGroupAccess(contacts, allowGroups) {
  if (!Array.isArray(contacts)) return [];
  if (allowGroups) return contacts;
  return contacts.filter((c) => !c?.isGroup);
}
